import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { DeviceDialogComponent } from '@components/device-dialog/device-dialog.component';
import { ModelDialogComponent } from '@components/model-dialog/model-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DeviceManagmentSocketService } from '@service/device-managment-socket.service';
import { DeviceManagmentService } from '@service/device-managment.service';
import { EnvironmentService } from '@service/environment.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { VesselService } from '@service/vessel.service';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';
import { DeviceManagmentEvents } from 'icreate/common/io-event/websocket-message';
import { DefaultDevices, Device, Model } from 'icreate/common/models/device';
import { fromEvent, lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-device-managment',
    templateUrl: './device-managment.component.html',
    styleUrl: './device-managment.component.css',
    standalone: false,
})
export class DeviceManagmentComponent implements OnInit, OnDestroy {
    @ViewChildren('optionsCol') optionsCol: QueryList<ElementRef>;

    deviceList: Device[] = [];
    selectedDeviceModels: Model[] = [];
    selectedDevice: Device;

    isLoading: boolean = false;
    isInfoMessageVisible: boolean = false;
    showDeviceOptions: boolean = false;

    openedModelOptions;
    optionsTop: string;
    optionsLeft: string;

    private expandedModelIds = new Set<string | number>();
    private destroy$: Subject<void> = new Subject();
    private env: RuntimeEnvironments;

    constructor(
        private dialog: MatDialog,
        private translate: TranslateService,
        private deviceManagmentService: DeviceManagmentService,
        private deviceManagmentSocketService: DeviceManagmentSocketService,
        private messageDialogService: MessageDialogService,
        private vesselService: VesselService,
        private router: Router,
        private environmentService: EnvironmentService
    ) {
        this.env = this.environmentService.getEnvironment();
    }

    @HostListener('document:click', [])
    clickOut() {
        this.isInfoMessageVisible = false;
        this.showDeviceOptions = false;
        this.openedModelOptions = null;
    }

    async ngOnInit(): Promise<void> {
        if (this.env == RuntimeEnvironments.ELECTRON) {
            await this.router.navigateByUrl('/home');
            return;
        }
        try {
            this.isLoading = true;
            this.vesselService.setActiveVessel(null);
            this.deviceList = await this.deviceManagmentService.getAll();
            if (this.deviceList && this.deviceList.length > 0) {
                this.deviceList = this.sortDeviceList(this.deviceList);
                this.selectedDevice = this.deviceList[0];
                this.selectedDeviceModels = this.selectedDevice.models;
            }
            this.deviceManagmentSocketService.startSubscriptions();
        } catch (err) {
            console.error(err);
        }
        this.isLoading = false;

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isInfoMessageVisible = false;
                    this.showDeviceOptions = false;
                    this.openedModelOptions = null;
                });
        }

        this.deviceManagmentSocketService
            .updateListSubject()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ list, socketData, eventType, openPage }) => {
                this.deviceList = this.sortDeviceList(list);
                this.showSocketMessage(socketData, eventType);
                if (eventType == DeviceManagmentEvents.DEVICE_DELETED) {
                    if (this.selectedDevice.id == socketData.id) {
                        if (this.deviceList.length > 0) this.selectedDevice = this.deviceList[0];
                        else this.selectedDevice = null;
                    }
                } else if (eventType == DeviceManagmentEvents.DEVICE_UPDATED) {
                    if (this.selectedDevice.id == socketData.id) {
                        this.selectedDevice = socketData;
                    }
                } else if (eventType == DeviceManagmentEvents.DEVICE_CREATED && openPage) {
                    this.selectDevice(socketData.device);
                }
            });
    }

    ngOnDestroy(): void {
        this.deviceManagmentSocketService.destroySubscriptions();
        this.destroy$.next();
        this.destroy$.complete();
    }

    async addDevice() {
        const dialogRef = this.dialog.open(DeviceDialogComponent, {
            width: '472px',
            data: {
                currentDevices: this.deviceList,
            },
            disableClose: true,
        });
        const newDeviceName = await lastValueFrom(dialogRef.afterClosed());
        if (!newDeviceName) return;

        try {
            await this.deviceManagmentService.createDevice(newDeviceName);
        } catch (err) {
            console.error(err);
        }
    }

    selectDevice(device: Device) {
        this.selectedDevice = device;
        this.selectedDeviceModels = this.selectedDevice.models;
    }

    async editDevice() {
        if (!this.selectedDevice) return;
        const dialogRef = this.dialog.open(DeviceDialogComponent, {
            width: '472px',
            data: {
                device: this.selectedDevice,
                currentDevices: this.deviceList,
            },
            disableClose: true,
        });
        const updatedDevice: Device = await lastValueFrom(dialogRef.afterClosed());
        if (!updatedDevice) return;
        try {
            await this.deviceManagmentService.updateDevice(updatedDevice, updatedDevice.id);
        } catch (err) {
            console.error(err);
        }
    }

    async deleteDevice() {
        if (!this.selectedDevice) return;
        const deleteTitle = this.translate.instant('device.delete.title', {
            device: this.selectedDevice.name,
        });
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: deleteTitle,
                message: 'device.delete.message',
                discardButton: 'dlg.cancel',
                saveButton: 'dlg.delete',
                width: '452px',
            },
            disableClose: true,
        });
        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            try {
                await this.deviceManagmentService.deleteDevice(this.selectedDevice);
            } catch (err) {
                console.error(err);
            }
        }
    }

    async addModel() {
        if (!this.selectedDevice) return;
        const dialogRef = this.dialog.open(ModelDialogComponent, {
            width: '472px',
            data: {
                selectedDevice: this.selectedDevice,
                currentModels: this.selectedDeviceModels,
            },
            disableClose: true,
        });

        const newModel: Model = await lastValueFrom(dialogRef.afterClosed());
        if (!newModel) return;

        try {
            await this.deviceManagmentService.createModel(newModel);
        } catch (err) {
            console.error(err);
        }
    }

    openModelOptions(model, index) {
        if (this.openedModelOptions == model) {
            this.openedModelOptions = null;
            return;
        }
        this.openedModelOptions = model;
        this.updatePosition(index);
    }

    async editModel(model) {
        if (!model || !this.openedModelOptions || this.openedModelOptions != model) return;

        const dialogRef = this.dialog.open(ModelDialogComponent, {
            width: '472px',
            data: {
                selectedDevice: this.selectedDevice,
                model: model,
                currentModels: this.selectedDeviceModels,
            },
            disableClose: true,
        });

        const updatedModel: Model = await lastValueFrom(dialogRef.afterClosed());
        if (!updatedModel) return;

        try {
            await this.deviceManagmentService.updateModel(updatedModel, model.id);
        } catch (err) {
            console.error(err);
        }
    }

    async deleteModel(model) {
        if (!model || !this.openedModelOptions || this.openedModelOptions != model) return;
        const deleteTitle = this.translate.instant('model.delete.title', { model: model.name });

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: deleteTitle,
                message: 'model.delete.message',
                discardButton: 'dlg.cancel',
                saveButton: 'dlg.delete',
                width: '452px',
            },
            disableClose: true,
        });
        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            try {
                await this.deviceManagmentService.deleteModel(model, model.id);
                this.expandedModelIds.delete(model.id);
            } catch (err) {
                console.error(err);
            }
        }
    }

    isModelExpanded(model: Model): boolean {
        return this.expandedModelIds.has(model.id);
    }

    setModelExpanded(model: Model, expanded: boolean): void {
        if (expanded) {
            this.expandedModelIds.add(model.id);
        } else {
            this.expandedModelIds.delete(model.id);
        }
    }

    private showSocketMessage(socketData: Device | Model, eventType) {
        let message = '';
        if (eventType == DeviceManagmentEvents.DEVICE_CREATED || eventType == DeviceManagmentEvents.MODEL_CREATED) {
            message = 'device-managment.created';
        } else if (
            eventType == DeviceManagmentEvents.DEVICE_UPDATED ||
            eventType == DeviceManagmentEvents.MODEL_UPDATED
        ) {
            message = 'device-managment.updated';
        } else if (
            eventType == DeviceManagmentEvents.DEVICE_DELETED ||
            eventType == DeviceManagmentEvents.MODEL_DELETED
        ) {
            message = 'device-managment.deleted';
        }

        this.messageDialogService.openMessage(message, { name: socketData.name });
    }

    private updatePosition(index) {
        if (!this.optionsCol) return;
        const optionsColArray = this.optionsCol.toArray();
        const selectedModel = optionsColArray ? optionsColArray[index] : null;
        if (!selectedModel) return;
        const modelPosition = selectedModel.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;

        this.optionsTop = modelPosition.bottom + scrollTop + 'px';
    }

    private sortDeviceList(deviceList: Device[]): Device[] {
        const orderMap = new Map(Object.values(DefaultDevices).map((val, i) => [val, i]));

        const inOrder = deviceList
            .filter(item => orderMap.has(item.name as any))
            .sort((a, b) => orderMap.get(a.name as any) - orderMap.get(b.name as any));

        const notInOrder = deviceList.filter(item => !orderMap.has(item.name as any));

        return [...inOrder, ...notInOrder];
    }

    get deviceHasNoModels() {
        if (!this.selectedDevice || !this.selectedDeviceModels) return false;
        return this.selectedDeviceModels?.length == 0;
    }
}
